
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaStar,
    FaRegStar,
    FaSearch,
    FaMoneyBillWave,
    FaMapMarkedAlt,
    FaUndo,
    FaSlidersH,
    FaTimes,
    FaArrowRight,
    FaSwimmingPool,
    FaWifi,
    FaUtensils,
    FaHotel,
    FaChevronRight,
    FaFilter,
    FaCheckCircle,
} from "react-icons/fa";

import hotelImg from "../assests/images/hotel-bg.jpg";

const COLORS = {
    orange: "#FF5A1F",
    yellow: "#F7B928",
    blue: "#0B6E8E",
    navy: "#002B45",
    background: "#F7F5EF",
    lightBlue: "#F4F8FC",
    white: "#FFFFFF",
    muted: "#71808A",
    border: "#DCE7ED",
};

const API_URL = "https://cheapdubaihotels.com/api";
const HOTEL_IMAGE_URL = "https://uaediscountedhotels.com/uploads/hotels/";

const getArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.hotels)) return data.hotels;
    if (Array.isArray(data?.destinations)) return data.destinations;
    return [];
};

const getHotelName = (hotel) =>
    hotel?.name || hotel?.hotel_name || hotel?.title || "Hotel in Dubai";

const getHotelLocation = (hotel) =>
    hotel?.location ||
    hotel?.address ||
    hotel?.destination?.name ||
    hotel?.city ||
    "Dubai, UAE";

const getHotelPrice = (hotel) =>
    Number(hotel?.price_per_night ?? hotel?.price ?? 0);

const getHotelRating = (hotel) =>
    Number(hotel?.rating ?? hotel?.average_rating ?? 4.5);

const getHotelImage = (hotel) => {
    if (!hotel?.image) return hotelImg;

    if (
        hotel.image.startsWith("http://") ||
        hotel.image.startsWith("https://")
    ) {
        return hotel.image;
    }

    return `${HOTEL_IMAGE_URL}${hotel.image}`;
};

const cleanDescription = (description) => {
    if (!description) {
        return "Enjoy a comfortable stay with convenient access to Dubai's attractions.";
    }

    return String(description)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

const formatPrice = (price) =>
    new Intl.NumberFormat("en-AE", {
        maximumFractionDigits: 0,
    }).format(price);

function StarRating({ rating = 4.5 }) {
    const roundedRating = Math.round(Number(rating) || 0);

    return (
        <div className="flex items-center gap-1" aria-label={`${rating} out of 5 stars`}>
            {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                    key={star}
                    className="text-[12px]"
                    style={{
                        color: star <= roundedRating
                            ? COLORS.yellow
                            : COLORS.border,
                    }}
                />
            ))}
        </div>
    );
}

function HotelCard({ hotel, onOpen, wishlisted, onWishlist }) {
    const [imageError, setImageError] = useState(false);

    const name = getHotelName(hotel);
    const location = getHotelLocation(hotel);
    const rating = getHotelRating(hotel);
    const price = getHotelPrice(hotel);
    const description = cleanDescription(
        hotel?.short_description || hotel?.description
    );

    return (

        <article
            className="group overflow-hidden rounded-[24px] border bg-white transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(0,43,69,0.11)]"
            style={{ borderColor: COLORS.border }}
        >
            {/* Clickable hotel image and header */}
            <div className="relative h-[220px] overflow-hidden sm:h-[250px]">
                <Link
                    to={`/hotels/${hotel.slug}`}
                    aria-label={`View details for ${name}`}
                    className="absolute inset-0 z-0 block"
                >
                    <img
                        src={imageError ? hotelImg : getHotelImage(hotel)}
                        alt={name}
                        onError={() => setImageError(true)}
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-[#002B45]/80 via-[#002B45]/10 to-[#002B45]/10" />

                    <div className="absolute left-4 top-4">
                        <span
                            className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-white shadow-sm"
                            style={{ backgroundColor: COLORS.navy }}
                        >
                            <FaHotel className="text-[11px]" />
                            Dubai Stay
                        </span>
                    </div>

                    <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                        <div className="min-w-0">
                            <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-white/90">
                                <FaMapMarkerAlt className="shrink-0" />
                                <span className="truncate">{location}</span>
                            </p>

                            <h3 className="line-clamp-1 text-xl font-extrabold text-white sm:text-2xl">
                                {name}
                            </h3>
                        </div>

                        <div className="shrink-0 rounded-xl bg-white px-3 py-2 text-center shadow-md">
                            <div
                                className="flex items-center justify-center gap-1 text-sm font-extrabold"
                                style={{ color: COLORS.navy }}
                            >
                                <FaStar style={{ color: COLORS.yellow }} />
                                {Number.isFinite(rating) ? rating.toFixed(1) : "4.5"}
                            </div>

                            <p
                                className="mt-0.5 text-[9px] font-bold uppercase tracking-wider"
                                style={{ color: COLORS.muted }}
                            >
                                Rating
                            </p>
                        </div>
                    </div>
                </Link>

                {/* Independent wishlist button */}
                <button
                    type="button"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onWishlist(hotel);
                    }}
                    aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
                    className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/70 bg-white/95 shadow-md transition hover:scale-110"
                    style={{ color: wishlisted ? COLORS.orange : COLORS.navy }}
                >
                    {wishlisted ? <FaHeart /> : <FaRegHeart />}
                </button>
            </div>

            {/* Entire details section is clickable */}
            <Link
                to={`/hotels/${hotel.slug}`}
                aria-label={`View details for ${name}`}
                className="block rounded-b-[24px] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#FF5A1F]"
            >
                <div className="p-5 sm:p-6">
                    {/* Rating and label */}
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <StarRating rating={rating} />

                            <span
                                className="text-xs font-semibold"
                                style={{ color: COLORS.muted }}
                            >
                                {Number.isFinite(rating) ? rating.toFixed(1) : "4.5"} / 5
                            </span>
                        </div>

                        <span
                            className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider"
                            style={{
                                backgroundColor: "#FFF1E9",
                                color: COLORS.orange,
                            }}
                        >
                            Popular choice
                        </span>
                    </div>

                    {/* Description */}
                    <p
                        className="mb-5 line-clamp-2 min-h-[40px] text-sm leading-5"
                        style={{ color: COLORS.muted }}
                    >
                        {description}
                    </p>

                    {/* Amenities */}
                    <div
                        className="mb-5 grid grid-cols-3 gap-2 border-y py-4"
                        style={{ borderColor: COLORS.border }}
                    >
                        <div className="flex flex-col items-center gap-2 text-center">
                            <span
                                className="flex h-9 w-9 items-center justify-center rounded-xl"
                                style={{
                                    backgroundColor: COLORS.lightBlue,
                                    color: COLORS.blue,
                                }}
                            >
                                <FaSwimmingPool />
                            </span>

                            <span
                                className="text-[10px] font-semibold"
                                style={{ color: COLORS.muted }}
                            >
                                Pool
                            </span>
                        </div>

                        <div className="flex flex-col items-center gap-2 text-center">
                            <span
                                className="flex h-9 w-9 items-center justify-center rounded-xl"
                                style={{
                                    backgroundColor: COLORS.lightBlue,
                                    color: COLORS.blue,
                                }}
                            >
                                <FaWifi />
                            </span>

                            <span
                                className="text-[10px] font-semibold"
                                style={{ color: COLORS.muted }}
                            >
                                Wi-Fi
                            </span>
                        </div>

                        <div className="flex flex-col items-center gap-2 text-center">
                            <span
                                className="flex h-9 w-9 items-center justify-center rounded-xl"
                                style={{
                                    backgroundColor: COLORS.lightBlue,
                                    color: COLORS.blue,
                                }}
                            >
                                <FaUtensils />
                            </span>

                            <span
                                className="text-[10px] font-semibold"
                                style={{ color: COLORS.muted }}
                            >
                                Dining
                            </span>
                        </div>
                    </div>

                    {/* Price and navigation indicator */}
                    <div className="flex items-end justify-between gap-3">
                        <div>
                            <p
                                className="mb-1 text-[10px] font-bold uppercase tracking-[0.16em]"
                                style={{ color: COLORS.muted }}
                            >
                                Starting from
                            </p>

                            <div className="flex items-baseline gap-1.5">
                                <span
                                    className="text-sm font-bold"
                                    style={{ color: COLORS.blue }}
                                >
                                    AED
                                </span>

                                <span
                                    className="text-3xl font-black tracking-tight"
                                    style={{ color: COLORS.navy }}
                                >
                                    {formatPrice(price)}
                                </span>
                            </div>

                            <p
                                className="mt-1 text-[11px]"
                                style={{ color: COLORS.muted }}
                            >
                                Per night
                            </p>
                        </div>

                        <span
                            className="mb-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white transition duration-300 group-hover:translate-x-1 group-hover:shadow-lg"
                            style={{ backgroundColor: COLORS.orange }}
                            aria-hidden="true"
                        >
                            <FaArrowRight />
                        </span>
                    </div>
                </div>
            </Link>
        </article>
    );
}

function HotelSkeleton() {
    return (
        <div
            className="overflow-hidden rounded-[24px] border bg-white"
            style={{ borderColor: COLORS.border }}
        >
            <div className="h-[230px] animate-pulse bg-slate-200" />
            <div className="space-y-4 p-6">
                <div className="h-4 w-1/3 animate-pulse rounded bg-slate-200" />
                <div className="h-6 w-3/4 animate-pulse rounded bg-slate-200" />
                <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
            </div>
        </div>
    );
}

function FilterSidebar({
    destinations,
    selectedDestinations,
    toggleDestination,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    rating,
    setRating,
    clearFilters,
    mobile = false,
    closeMobile,
}) {
    const selectedIds = selectedDestinations.map((item) => String(item.id));

    return (
        <aside
            className={`rounded-[24px] border bg-white p-5 sm:p-6 ${mobile ? "h-full overflow-y-auto rounded-none border-0" : "sticky top-6"
                }`}
            style={{
                borderColor: COLORS.border,
                boxShadow: mobile ? "none" : "0 10px 35px rgba(0,43,69,0.045)",
            }}
        >
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <p
                        className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.18em]"
                        style={{ color: COLORS.orange }}
                    >
                        Personalise
                    </p>
                    <h2 className="text-xl font-black" style={{ color: COLORS.navy }}>
                        Filters
                    </h2>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="inline-flex items-center gap-1.5 text-xs font-bold transition hover:opacity-70"
                        style={{ color: COLORS.blue }}
                    >
                        <FaUndo />
                        Reset
                    </button>

                    {mobile && (
                        <button
                            type="button"
                            onClick={closeMobile}
                            aria-label="Close filters"
                            className="ml-1 flex h-9 w-9 items-center justify-center rounded-full"
                            style={{ backgroundColor: COLORS.lightBlue, color: COLORS.navy }}
                        >
                            <FaTimes />
                        </button>
                    )}
                </div>
            </div>

            <div className="mb-6">
                <div className="mb-4 flex items-center gap-2">
                    <span
                        className="flex h-9 w-9 items-center justify-center rounded-xl"
                        style={{ backgroundColor: COLORS.lightBlue, color: COLORS.blue }}
                    >
                        <FaMapMarkedAlt />
                    </span>
                    <h3 className="text-sm font-extrabold" style={{ color: COLORS.navy }}>
                        Destination
                    </h3>
                </div>

                <div className="space-y-3">
                    {destinations.length > 0 ? (
                        destinations.map((destination) => {
                            const checked = selectedIds.includes(String(destination.id));

                            return (
                                <label
                                    key={destination.id}
                                    className="flex cursor-pointer items-center gap-3 text-sm transition hover:opacity-75"
                                    style={{ color: COLORS.muted }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleDestination(destination)}
                                        className="h-4 w-4 cursor-pointer rounded accent-[#FF5A1F]"
                                    />
                                    <span className="flex-1">
                                        {destination.name || destination.title || "Destination"}
                                    </span>
                                </label>
                            );
                        })
                    ) : (
                        <p className="text-xs leading-5" style={{ color: COLORS.muted }}>
                            Destination options will appear here when available.
                        </p>
                    )}
                </div>
            </div>

            <div className="my-6 h-px" style={{ backgroundColor: COLORS.border }} />

            <div className="mb-6">
                <div className="mb-4 flex items-center gap-2">
                    <span
                        className="flex h-9 w-9 items-center justify-center rounded-xl"
                        style={{ backgroundColor: COLORS.lightBlue, color: COLORS.blue }}
                    >
                        <FaMoneyBillWave />
                    </span>
                    <h3 className="text-sm font-extrabold" style={{ color: COLORS.navy }}>
                        Price per night
                    </h3>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <span className="mb-2 block text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.muted }}>
                            Minimum
                        </span>
                        <div
                            className="flex items-center rounded-xl border px-3"
                            style={{ borderColor: COLORS.border, backgroundColor: COLORS.lightBlue }}
                        >
                            <span className="text-[10px] font-bold" style={{ color: COLORS.blue }}>
                                AED
                            </span>
                            <input
                                type="number"
                                min="0"
                                value={minPrice}
                                onChange={(event) =>
                                    setMinPrice(Math.max(0, Number(event.target.value) || 0))
                                }
                                className="w-full min-w-0 bg-transparent px-2 py-3 text-sm font-bold outline-none"
                                style={{ color: COLORS.navy }}
                                aria-label="Minimum price"
                            />
                        </div>
                    </label>

                    <label className="block">
                        <span className="mb-2 block text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.muted }}>
                            Maximum
                        </span>
                        <div
                            className="flex items-center rounded-xl border px-3"
                            style={{ borderColor: COLORS.border, backgroundColor: COLORS.lightBlue }}
                        >
                            <span className="text-[10px] font-bold" style={{ color: COLORS.blue }}>
                                AED
                            </span>
                            <input
                                type="number"
                                min="0"
                                value={maxPrice}
                                onChange={(event) =>
                                    setMaxPrice(Math.max(0, Number(event.target.value) || 0))
                                }
                                className="w-full min-w-0 bg-transparent px-2 py-3 text-sm font-bold outline-none"
                                style={{ color: COLORS.navy }}
                                aria-label="Maximum price"
                            />
                        </div>
                    </label>
                </div>

                <div className="mt-4 flex items-center justify-between text-xs font-semibold" style={{ color: COLORS.muted }}>
                    <span>AED {formatPrice(minPrice)}</span>
                    <span>AED {formatPrice(maxPrice)}</span>
                </div>
            </div>

            <div className="my-6 h-px" style={{ backgroundColor: COLORS.border }} />

            <div>
                <div className="mb-4 flex items-center gap-2">
                    <span
                        className="flex h-9 w-9 items-center justify-center rounded-xl"
                        style={{ backgroundColor: "#FFF7DF", color: "#B8860B" }}
                    >
                        <FaStar />
                    </span>
                    <h3 className="text-sm font-extrabold" style={{ color: COLORS.navy }}>
                        Guest rating
                    </h3>
                </div>

                <div className="space-y-2">
                    {[0, 3, 4, 4.5].map((value) => {
                        const active = Number(rating) === value;

                        return (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setRating(value)}
                                className="flex w-full items-center justify-between rounded-xl border px-3 py-3 text-left transition"
                                style={{
                                    borderColor: active ? COLORS.orange : COLORS.border,
                                    backgroundColor: active ? "#FFF1E9" : COLORS.white,
                                    color: active ? COLORS.navy : COLORS.muted,
                                }}
                            >
                                <span className="flex items-center gap-2 text-xs font-bold">
                                    {value === 0 ? (
                                        "Any rating"
                                    ) : (
                                        <>
                                            <span className="flex items-center gap-1" style={{ color: COLORS.yellow }}>
                                                <FaStar />
                                                {value}+
                                            </span>
                                            <span>stars</span>
                                        </>
                                    )}
                                </span>

                                {active && (
                                    <FaCheckCircle style={{ color: COLORS.orange }} />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {mobile && (
                <button
                    type="button"
                    onClick={closeMobile}
                    className="mt-7 w-full rounded-full py-4 text-sm font-extrabold text-white"
                    style={{ backgroundColor: COLORS.orange }}
                >
                    Show Hotels
                </button>
            )}
        </aside>
    );
}

export default function HotelList({
    limit,
    showViewAllButton = false,
    showSidebar = true,
    showBanner = false,
    showHeading = false,
}) {
    const navigate = useNavigate();
    const location = useLocation();

    const [hotels, setHotels] = useState([]);
    const [destinations, setDestinations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(5000);
    const [rating, setRating] = useState(0);
    const [selectedDestinations, setSelectedDestinations] = useState([]);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [wishlist, setWishlist] = useState(() => {
        try {
            const saved = localStorage.getItem("wishlist");
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    const shouldShowHeading =
        showHeading || location.pathname === "/hotels";

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(search.trim());
        }, 500);

        return () => clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        let active = true;

        const fetchDestinations = async () => {
            try {
                const response = await axios.get(`${API_URL}/destinations`);

                if (active) {
                    setDestinations(getArray(response.data));
                }
            } catch (fetchError) {
                console.error("Error fetching destinations:", fetchError);
                if (active) setDestinations([]);
            }
        };

        fetchDestinations();

        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        let active = true;

        const fetchHotels = async () => {
            setLoading(true);
            setError("");

            try {
                const response = await axios.get(`${API_URL}/hotels`, {
                    params: {
                        search: debouncedSearch,
                        destinations: selectedDestinations.map((item) => item.id),
                        minPrice,
                        maxPrice,
                        rating,
                    },
                });

                if (active) {
                    setHotels(getArray(response.data));
                }
            } catch (fetchError) {
                console.error("Error fetching hotels:", fetchError);

                if (active) {
                    setHotels([]);
                    setError(
                        "We couldn't load hotels right now. Please check your connection and try again."
                    );
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchHotels();

        return () => {
            active = false;
        };
    }, [
        debouncedSearch,
        selectedDestinations,
        minPrice,
        maxPrice,
        rating,
    ]);

    useEffect(() => {
        try {
            localStorage.setItem("wishlist", JSON.stringify(wishlist));
        } catch (storageError) {
            console.error("Unable to save wishlist:", storageError);
        }
    }, [wishlist]);

    const toggleDestination = (destination) => {
        setSelectedDestinations((current) => {
            const exists = current.some(
                (item) => String(item.id) === String(destination.id)
            );

            return exists
                ? current.filter(
                    (item) => String(item.id) !== String(destination.id)
                )
                : [...current, destination];
        });
    };

    const isWishlisted = (hotel) =>
        wishlist.some((item) => {
            if (typeof item === "string" || typeof item === "number") {
                return String(item) === String(hotel.id);
            }

            return (
                String(item?.id) === String(hotel.id) ||
                (item?.slug && item.slug === hotel.slug)
            );
        });

    const toggleWishlist = (hotel) => {
        setWishlist((current) => {
            const exists = current.some((item) => {
                if (typeof item === "string" || typeof item === "number") {
                    return String(item) === String(hotel.id);
                }

                return (
                    String(item?.id) === String(hotel.id) ||
                    (item?.slug && item.slug === hotel.slug)
                );
            });

            if (exists) {
                return current.filter((item) => {
                    if (typeof item === "string" || typeof item === "number") {
                        return String(item) !== String(hotel.id);
                    }

                    return (
                        String(item?.id) !== String(hotel.id) &&
                        !(item?.slug && item.slug === hotel.slug)
                    );
                });
            }

            return [
                ...current,
                {
                    id: hotel.id,
                    slug: hotel.slug,
                    name: getHotelName(hotel),
                    image: hotel.image,
                    price_per_night: getHotelPrice(hotel),
                },
            ];
        });
    };

    const clearFilters = () => {
        setSearch("");
        setDebouncedSearch("");
        setMinPrice(0);
        setMaxPrice(5000);
        setRating(0);
        setSelectedDestinations([]);
    };

    const openHotel = (hotel) => {
        if (hotel?.slug) {
            navigate(`/hotels/${hotel.slug}`);
        }
    };

    const visibleHotels = useMemo(() => {
        if (limit && Number(limit) > 0) {
            return hotels.slice(0, Number(limit));
        }

        return hotels;
    }, [hotels, limit]);

    const activeFilterCount =
        selectedDestinations.length +
        (minPrice > 0 ? 1 : 0) +
        (maxPrice < 5000 ? 1 : 0) +
        (rating > 0 ? 1 : 0);

    return (
        <div className="min-h-screen mt-28">
            {/* Redesigned hero banner */}
            {showBanner && (

                <section className="relative isolate overflow-hidden">
                    <div
                        className="absolute inset-0 -z-20 bg-cover bg-center"
                        style={{ backgroundImage: `url(${hotelImg})` }}
                    />

                    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#002B45]/95 via-[#002B45]/85 to-[#002B45]/45" />

                    <div className="absolute -right-16 -top-20 -z-10 h-56 w-56 rounded-full border-[30px] border-white/5" />
                    <div className="absolute -bottom-24 right-[22%] -z-10 h-48 w-48 rounded-full border-[25px] border-[#F7B928]/10" />

                    <div className="mx-auto max-w-[1536px] px-5 py-7 sm:px-8 sm:py-9 lg:px-12 lg:py-10 xl:px-16">
                        <div className="max-w-3xl">
                            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 backdrop-blur-sm">
                                <span
                                    className="h-1.5 w-1.5 rounded-full"
                                    style={{ backgroundColor: COLORS.yellow }}
                                />
                                <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-white">
                                    Your Dubai getaway starts here
                                </span>
                            </div>

                            <h1 className="max-w-2xl text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                                Find Your{" "}
                                <span style={{ color: COLORS.yellow }}>
                                    Perfect Hotel
                                </span>
                            </h1>

                            <p className="mt-2 max-w-xl text-xs leading-6 text-white/75 sm:text-sm">
                                Discover exceptional stays, compare your options, and find the right hotel for your next Dubai escape.
                            </p>

                            <div className="mt-5 flex flex-col gap-2 rounded-2xl bg-white p-2 shadow-[0_15px_40px_rgba(0,0,0,0.2)] sm:flex-row sm:items-center sm:gap-3 sm:rounded-full sm:pl-5">
                                <div className="flex min-w-0 flex-1 items-center gap-3 px-2">
                                    <FaSearch style={{ color: COLORS.blue }} />

                                    <input
                                        type="search"
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search hotels, areas, or destinations..."
                                        className="w-full bg-transparent py-2.5 text-sm outline-none"
                                        style={{ color: COLORS.navy }}
                                    />

                                    {search && (
                                        <button
                                            type="button"
                                            onClick={() => setSearch("")}
                                            aria-label="Clear search"
                                            style={{ color: COLORS.muted }}
                                        >
                                            <FaTimes />
                                        </button>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    onClick={() => {
                                        document
                                            .getElementById("hotel-results")
                                            ?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                    className="flex items-center justify-center gap-2 rounded-full px-6 py-3 text-xs font-extrabold text-white transition hover:opacity-90 sm:text-sm"
                                    style={{ backgroundColor: COLORS.orange }}
                                >
                                    Search Hotels
                                    <FaArrowRight />
                                </button>
                            </div>

                            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-medium text-white/80">
                                <span className="flex items-center gap-2">
                                    <FaCheckCircle style={{ color: COLORS.yellow }} />
                                    Handpicked stays
                                </span>

                                <span className="flex items-center gap-2">
                                    <FaCheckCircle style={{ color: COLORS.yellow }} />
                                    Great-value prices
                                </span>

                                <span className="flex items-center gap-2">
                                    <FaCheckCircle style={{ color: COLORS.yellow }} />
                                    Easy hotel discovery
                                </span>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <main
                id="hotel-results"
                className="mx-auto max-w-[1536px] px-5 py-10 sm:px-8 sm:py-12 lg:px-12 lg:py-16 xl:px-16"
            >
                {/* Page heading */}
                {shouldShowHeading && (
                    <div className="mb-8 flex flex-col justify-between gap-5 md:mb-10 md:flex-row md:items-end">
                        <div>
                            <div className="mb-3 flex items-center gap-2">
                                <span className="h-[3px] w-8 rounded-full" style={{ backgroundColor: COLORS.orange }} />
                                <span className="h-[3px] w-5 rounded-full" style={{ backgroundColor: COLORS.yellow }} />
                                <span
                                    className="text-[10px] font-extrabold uppercase tracking-[0.2em]"
                                    style={{ color: COLORS.blue }}
                                >
                                    Dubai Marina Collection
                                </span>
                            </div>

                            <h2
                                className="text-3xl font-black leading-tight tracking-tight sm:text-4xl lg:text-[42px]"
                                style={{ color: COLORS.navy }}
                            >
                                Find Your Perfect
                                <span style={{ color: COLORS.orange }}> Stay</span>
                            </h2>

                            <p className="mt-3 max-w-2xl text-sm leading-6 sm:text-base" style={{ color: COLORS.muted }}>
                                Explore our selection of hotels and discover a stay that suits your plans and budget.
                            </p>
                        </div>

                        <div
                            className="flex w-fit items-center gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm"
                            style={{ borderColor: COLORS.border }}
                        >
                            <span
                                className="flex h-11 w-11 items-center justify-center rounded-xl"
                                style={{ backgroundColor: COLORS.lightBlue, color: COLORS.blue }}
                            >
                                <FaHotel className="text-lg" />
                            </span>
                            <div>
                                <p className="text-xl font-black" style={{ color: COLORS.navy }}>
                                    {loading ? "..." : hotels.length}
                                </p>
                                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.muted }}>
                                    Hotels found
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Search and mobile filter toolbar */}
                {!showBanner && (
                    <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div
                            className="flex min-w-0 flex-1 items-center gap-3 rounded-full border bg-white px-5 py-4 shadow-sm transition focus-within:border-[#0B6E8E]"
                            style={{ borderColor: COLORS.border }}
                        >
                            <FaSearch style={{ color: COLORS.blue }} />
                            <input
                                type="search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search hotels or destinations..."
                                className="w-full bg-transparent text-sm outline-none"
                                style={{ color: COLORS.navy }}
                            />
                            {search && (
                                <button
                                    type="button"
                                    onClick={() => setSearch("")}
                                    aria-label="Clear search"
                                    style={{ color: COLORS.muted }}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        {showSidebar && (
                            <button
                                type="button"
                                onClick={() => setMobileFiltersOpen(true)}
                                className="inline-flex items-center justify-center gap-2 rounded-full border bg-white px-5 py-4 text-sm font-bold shadow-sm transition hover:shadow-md lg:hidden"
                                style={{ borderColor: COLORS.border, color: COLORS.navy }}
                            >
                                <FaSlidersH style={{ color: COLORS.orange }} />
                                Filters
                                {activeFilterCount > 0 && (
                                    <span
                                        className="flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-extrabold text-white"
                                        style={{ backgroundColor: COLORS.orange }}
                                    >
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                )}

                {/* Results toolbar */}
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h3 className="text-lg font-extrabold" style={{ color: COLORS.navy }}>
                            {search ? "Search results" : "Explore our hotels"}
                        </h3>
                        <p className="mt-1 text-xs sm:text-sm" style={{ color: COLORS.muted }}>
                            {loading
                                ? "Finding the best stays for you..."
                                : `${visibleHotels.length} ${visibleHotels.length === 1 ? "hotel" : "hotels"} displayed`}
                        </p>
                    </div>

                    {activeFilterCount > 0 && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-bold transition hover:opacity-75"
                            style={{ backgroundColor: "#FFF1E9", color: COLORS.orange }}
                        >
                            <FaUndo />
                            Clear filters
                        </button>
                    )}
                </div>

                <div className={`grid grid-cols-1 items-start gap-6 lg:gap-8 ${showSidebar ? "lg:grid-cols-[270px_minmax(0,1fr)] xl:grid-cols-[290px_minmax(0,1fr)]" : ""
                    }`}>
                    {/* Desktop sidebar */}
                    {showSidebar && (
                        <div className="hidden lg:block">
                            <FilterSidebar
                                destinations={destinations}
                                selectedDestinations={selectedDestinations}
                                toggleDestination={toggleDestination}
                                minPrice={minPrice}
                                setMinPrice={setMinPrice}
                                maxPrice={maxPrice}
                                setMaxPrice={setMaxPrice}
                                rating={rating}
                                setRating={setRating}
                                clearFilters={clearFilters}
                            />
                        </div>
                    )}

                    {/* Hotel cards */}
                    <div className="min-w-0">
                        {error ? (
                            <div
                                className="rounded-[24px] border bg-white px-6 py-14 text-center sm:px-10"
                                style={{ borderColor: COLORS.border }}
                            >
                                <span
                                    className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl text-2xl"
                                    style={{ backgroundColor: COLORS.lightBlue, color: COLORS.blue }}
                                >
                                    <FaHotel />
                                </span>
                                <h3 className="text-xl font-black" style={{ color: COLORS.navy }}>
                                    Unable to load hotels
                                </h3>
                                <p className="mx-auto mt-3 max-w-md text-sm leading-6" style={{ color: COLORS.muted }}>
                                    {error}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => window.location.reload()}
                                    className="mt-6 rounded-full px-6 py-3 text-sm font-bold text-white"
                                    style={{ backgroundColor: COLORS.orange }}
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : loading ? (
                            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                                {[1, 2, 3, 4].slice(0, limit || 4).map((item) => (
                                    <HotelSkeleton key={item} />
                                ))}
                            </div>
                        ) : visibleHotels.length > 0 ? (
                            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                                {visibleHotels.map((hotel, index) => (
                                    <div
                                        key={hotel.id ?? hotel.slug ?? index}
                                        className="animate-[fadeInUp_0.45s_ease-out_both]"
                                        style={{ animationDelay: `${Math.min(index * 70, 350)}ms` }}
                                    >
                                        <HotelCard
                                            hotel={hotel}
                                            onOpen={openHotel}
                                            wishlisted={isWishlisted(hotel)}
                                            onWishlist={toggleWishlist}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div
                                className="rounded-[24px] border bg-white px-6 py-14 text-center sm:px-10 sm:py-20"
                                style={{ borderColor: COLORS.border }}
                            >
                                <span
                                    className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl text-2xl"
                                    style={{ backgroundColor: COLORS.lightBlue, color: COLORS.blue }}
                                >
                                    <FaSearch />
                                </span>
                                <p
                                    className="mb-2 text-[10px] font-extrabold uppercase tracking-[0.2em]"
                                    style={{ color: COLORS.orange }}
                                >
                                    No matches found
                                </p>
                                <h3 className="text-2xl font-black" style={{ color: COLORS.navy }}>
                                    Let's try another search
                                </h3>
                                <p className="mx-auto mt-3 max-w-md text-sm leading-6" style={{ color: COLORS.muted }}>
                                    We couldn't find hotels matching your current filters. Adjust your search or reset the filters to explore all available stays.
                                </p>
                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-extrabold text-white transition hover:opacity-90"
                                    style={{ backgroundColor: COLORS.orange }}
                                >
                                    <FaUndo />
                                    Reset All Filters
                                </button>
                            </div>
                        )}

                        {!loading &&
                            !error &&
                            hotels.length > 0 &&
                            (showViewAllButton || shouldShowHeading) && (
                                <div className="mt-10 text-center">
                                    <button
                                        type="button"
                                        onClick={() => navigate("/hotels")}
                                        className="inline-flex items-center gap-3 rounded-full border-2 bg-white px-8 py-4 text-sm font-extrabold transition hover:-translate-y-0.5 hover:shadow-lg"
                                        style={{ borderColor: COLORS.navy, color: COLORS.navy }}
                                    >
                                        View All Hotels
                                        <FaArrowRight style={{ color: COLORS.orange }} />
                                    </button>
                                </div>
                            )}
                    </div>
                </div>
            </main>

            {/* Mobile filter drawer */}
            {showSidebar && mobileFiltersOpen && (
                <div className="fixed inset-0 z-[100] lg:hidden">
                    <button
                        type="button"
                        aria-label="Close filters"
                        onClick={() => setMobileFiltersOpen(false)}
                        className="absolute inset-0 h-full w-full bg-[#002B45]/60 backdrop-blur-[2px]"
                    />

                    <div className="absolute bottom-0 right-0 top-0 w-[88%] max-w-[380px] animate-[slideInRight_0.25s_ease-out] bg-white shadow-2xl">
                        <FilterSidebar
                            destinations={destinations}
                            selectedDestinations={selectedDestinations}
                            toggleDestination={toggleDestination}
                            minPrice={minPrice}
                            setMinPrice={setMinPrice}
                            maxPrice={maxPrice}
                            setMaxPrice={setMaxPrice}
                            rating={rating}
                            setRating={setRating}
                            clearFilters={clearFilters}
                            mobile
                            closeMobile={() => setMobileFiltersOpen(false)}
                        />
                    </div>
                </div>
            )}

            <style>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(12px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
            `}</style>
        </div>
    );
}